import { SimpleDate } from "@repo/lib/simpleDate";
import { wholeNumber } from "./utils";

export type RecurrenceInterval = "daily" | "weekly" | "monthly" | "yearly";

export const intervalOptions: RecurrenceInterval[] = [
  "daily",
  "weekly",
  "monthly",
  "yearly",
];
export const intervalNames: Record<RecurrenceInterval, string> = {
  daily: "Days",
  weekly: "Weeks",
  monthly: "Months",
  yearly: "Years",
};
export const weekdayIndexOptions: string[] = [
  "1",
  "2",
  "3",
  "4",
  "5",
  "-1",
  "-2",
  "-3",
  "-4",
  "-5",
];
export const weekdayIndexNames: Record<string, string> = {
  "1": "First",
  "2": "Second",
  "3": "Third",
  "4": "Fourth",
  "5": "Fifth",
  "-1": "Last",
  "-2": "Second-to-last",
  "-3": "Third-to-last",
  "-4": "Fourth-to-last",
  "-5": "Fifth-to-last",
};
export const weekdays: string[] = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

export interface RecurrenceSettings {
  // recurring interval type
  interval: RecurrenceInterval;
  // start date (may not be the first occurrence, but the first occurrence cannot come before this day)
  startDate: Date;
  // how many intervals to skip between each occurrence
  skip: number | string;
  // a selection of weekdays (only applies when interval == "weekly")
  weekdaySelection: boolean[];
  // a selection of months (only applies when interval == "yearly")
  monthSelection: boolean[];
  // if to use the nth weekday of the month (only applies when interval == "monthly" | "yearly")
  nthWeekday: boolean;
  // the nth weekday of the month; negative indicates nth-to-last weekday of the month; 0 is invalid (only applies when interval == "monthly" | "yearly")
  weekdayIndex: number;
  // the weekday to pick of the nth weekday (only applies when interval == "monthly" | "yearly")
  weekdayDay: number;
  // the mode indicating when to stop: "count": recur a specific number of times, "until": continue until a date
  mode: "count" | "until";
  // indicates the specific number of recurrences (only applies when mode == "count")
  count: number | string;
  // indicates the date after which to stop recurring; this date could be the last occurrence (only applies when mode == "until")
  until: Date;
}

export const computeReoccurrences = ({
  values,
  skipDate,
  maxRecurrences,
}: {
  values: RecurrenceSettings;
  skipDate?: Date;
  maxRecurrences: number;
}): Date[] => {
  if (!values.startDate) return [];

  const skip = wholeNumber(values.skip); // force skip to be an integer greater than 0
  const count = wholeNumber(values.count);

  const nthWeekday = (date: SimpleDate) => {
    const { weekdayDay, weekdayIndex } = values;

    if (weekdayIndex === 0) return false; // 0 is invalid

    const year = date.year;
    const month = date.month;

    if (weekdayIndex > 0) {
      // jump to start of month
      date.day = 1;
      // if weekday of first of month is past desired weekday, jump a week forward
      if (date.weekday > weekdayDay) date.epoch += 7;
      // jump to week start, jump 'index' weeks ahead, then jump to desired weekday
      date.epoch += -date.weekday + (weekdayIndex - 1) * 7 + weekdayDay;
    } else {
      // jump to end of month
      date.day = date.daysInMonth;
      // if weekday of last of month is before desired weekday, jump a week back
      if (date.weekday < weekdayDay) date.epoch -= 7;
      // jump to week start, jump 'index' weeks back (note: it's negative), then jump to desired weekday
      date.epoch += -date.weekday + (weekdayIndex + 1) * 7 + weekdayDay;
    }

    return date.year === year && date.month === month;
  };

  const invalidNthWeekdays = () =>
    values.nthWeekday &&
    (values.weekdayIndex === 0 || Math.abs(values.weekdayIndex) > 5);

  const start = new SimpleDate(values.startDate);
  const date = new SimpleDate(values.startDate);
  const end = new SimpleDate(values.until);
  const skipped = skipDate ? new SimpleDate(skipDate) : null;

  let index = 0;
  const dates: Date[] = [];

  const pushNext = (date: SimpleDate) => {
    if (dates.length >= maxRecurrences) return true; // end after max recurrences
    if (date.epoch < start.epoch) return false; // skip occurrences before start
    if (skipped && date.epoch === skipped.epoch) return false; // jump over skipped day
    if (values.mode === "until" && date.epoch > end.epoch) return true; // end after end date
    if (values.mode === "count" && index >= count) return true; // end after end count

    dates.push(date.date); // push day

    index++; // increment index
    if (values.mode === "count" && index >= count) return true; // end after end count
    return false; // not at end yet
  };

  switch (values.interval) {
    case "daily": {
      let done = false;
      while (!done) {
        done ||= pushNext(date);
        date.epoch += skip; // next nth day
      }
      break;
    }

    case "weekly": {
      // normalize weekday selection
      const selection: boolean[] = [];
      for (let weekday = 0; weekday < 7; weekday++) {
        selection[weekday] = !!values.weekdaySelection[weekday];
      }
      if (!selection.includes(true)) return []; // no days were selected; bail

      date.epoch -= date.weekday; // jump to start of week

      let done = false;
      while (!done) {
        // iterate days in week, and add selected
        for (let weekday = 0; weekday < 7; weekday++) {
          if (!selection[weekday]) continue;

          const weekdayDay = new SimpleDate(date.epoch + weekday);

          done ||= pushNext(weekdayDay);
          if (done) break;
        }
        date.epoch += 7 * skip; // next nth week
      }
      break;
    }

    case "monthly": {
      const month = new SimpleDate(date.year, date.month, 1);
      const monthDay = date.day;

      if (invalidNthWeekdays()) return [];

      let done = false;
      while (!done) {
        const day = new SimpleDate(month);

        if (values.nthWeekday) {
          // apply nth weekday
          if (nthWeekday(day)) {
            done ||= pushNext(day);
          }
        } else {
          // jump to the same day of the month; pull back if month has fewer days
          day.day = Math.min(date.daysInMonth, monthDay);
          done ||= pushNext(day);
        }

        month.month += skip;
      }
      break;
    }

    case "yearly": {
      // normalize month selection
      const selection: boolean[] = [];
      for (let month = 0; month < 12; month++) {
        selection[month] = !!values.monthSelection[month];
      }
      if (!selection.includes(true)) return [];

      if (invalidNthWeekdays()) return [];

      let done = false;
      while (!done) {
        for (let month = 0; month < 12; month++) {
          if (!selection[month]) continue;

          const day = new SimpleDate(date.year, month + 1, 1);

          if (values.nthWeekday) {
            // apply nth weekday
            if (nthWeekday(day)) {
              done ||= pushNext(day);
            }
          } else {
            // jump to the same day of the month; pull back if month has fewer days
            day.day = Math.min(day.daysInMonth, date.day);
            done ||= pushNext(day);
          }
        }
        date.year += skip; // next nth year
      }
      break;
    }
  }

  return dates;
};

export const tweakSelections = (
  oldDate: Date,
  newDate: Date,
  weekdaySelection: boolean[],
  monthSelection: boolean[]
): [boolean[], boolean[]] => {
  const weekdayCount = weekdaySelection.reduce((a, v) => a + +!!v, 0);
  const monthCount = monthSelection.reduce((a, v) => a + +!!v, 0);

  const old = new SimpleDate(oldDate);
  const now = new SimpleDate(newDate);

  if (weekdayCount < 2) {
    if (!weekdayCount || weekdaySelection[old.weekday]) {
      weekdaySelection[old.weekday] = false;
      weekdaySelection[now.weekday] = true;
    }
  }
  if (monthCount < 2) {
    if (!monthCount || monthSelection[old.month - 1]) {
      monthSelection[old.month - 1] = false;
      monthSelection[now.month - 1] = true;
    }
  }
  return [weekdaySelection, monthSelection];
};
